import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { IS_TEST } from "../config";
import CompactYouTubePlayer from "../components/CompactYouTubePlayer";
import StoryCard, { SaveIndicator } from "../components/episode/StoryCard";
import PromoPanel from "../components/episode/PromoPanel";
import useStoryEditing from "../hooks/useStoryEditing";
import useEpisodeCollab from "../hooks/useEpisodeCollab";
import {
  authClient, usernameToEmail, profileFromUser, dataClientFor, canEdit, isAdmin, decodeEditorName,
} from "../lib/editorSession";
import { secToHMS, timeToSec } from "../utils/time";
import {
  collectMarkers, normaliseMarkers, chapterTime, sliceTranscript, YT_MIN_CHAPTERS,
} from "../utils/chapters";
import {
  FaArrowLeft, FaPlay, FaClock, FaSyncAlt, FaCogs, FaYoutube, FaCheckCircle,
  FaExclamationTriangle, FaSpinner, FaCut, FaSignOutAlt, FaHistory, FaCircle,
} from "react-icons/fa";

// Episode Editor — one page per episode (2026-09-28):
//   • Pipeline: run the post-episode processing (Zoom ingest → transcribe →
//     analyze, one step per Lambda invocation) and re-run single steps.
//   • Timeline: host intro, and per storyteller their intro / story /
//     discussion, prefilled by the pipeline and adjustable against the video.
//   • Chapters: live preview (utils/chapters.js mirrors the Lambda exactly);
//     published to YouTube only after a human reviews them.
//   • Stories: name, writer, genre, transcript, analysis (shared StoryCard).
// Jobs go through episode_jobs; its INSERT webhook starts the processor.

const ENV = IS_TEST ? "test" : "prod";
const POLL_MS = 10000;
// Same threshold as the processor's sweep_stuck_jobs: a job still "running"
// after this long died with its Lambda (e.g. the 15-minute limit) and must
// not lock the page. The next job the processor starts marks it failed.
const STUCK_MIN = 30;
const isStuck = (j) => j.status === "running" && Date.now() - new Date(j.updated_at || j.created_at).getTime() > STUCK_MIN * 60000;
const isActive = (j) => (j.status === "pending" || j.status === "running") && !isStuck(j);

const STEP_LABELS = {
  zoom_ingest: "Zoom ingest", download: "YouTube download", transcribe: "Transcribe",
  analyze: "Analyze", chapters: "Publish chapters",
};

const card = "border border-kk-gold/15 rounded-xl bg-white/[0.02]";
const btn = "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95 disabled:opacity-40";

function ago(iso) {
  if (!iso) return "";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

// Where the episode really is, for the header badge. episodes.status stops
// at "youtube_scheduled" (nothing advances it after the show), so read the
// pipeline instead: published > running > analysed > failed > aired.
function episodeStage(episode, latestRun, activeJobs) {
  if (latestRun.chapters?.status === "done") return "chapters published";
  if (activeJobs > 0) return "processing";
  if (latestRun.analyze?.status === "done") return "ready for review";
  if (["zoom_ingest", "download", "transcribe", "analyze"].some((k) => latestRun[k]?.status === "failed")) return "needs attention";
  const start = episode.scheduled_start || (episode.publish_date && `${episode.publish_date}T23:59:00`);
  if (start && new Date(start) < new Date()) return "aired";
  if (episode.status === "draft") return "draft";
  return "scheduled";
}

const StatusPill = ({ status }) => {
  const map = {
    done: "bg-green-500/15 text-green-300", failed: "bg-red-500/15 text-red-300",
    running: "bg-amber-500/15 text-amber-300", pending: "bg-sky-500/15 text-sky-300",
    canceled: "bg-white/10 text-white/40", replaced: "bg-white/10 text-white/40", stuck: "bg-red-500/10 text-red-300/80",
    // episode stages (episodeStage)
    draft: "bg-white/10 text-white/50", scheduled: "bg-sky-500/15 text-sky-300", aired: "bg-white/10 text-white/60",
    processing: "bg-amber-500/15 text-amber-300", "needs attention": "bg-red-500/15 text-red-300",
    "ready for review": "bg-kk-gold/15 text-kk-gold", "chapters published": "bg-green-500/15 text-green-300",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${map[status] || "bg-white/10 text-white/50"}`}>{status}</span>;
};

// ─── One editable time (seconds) with player helpers ─────────────────────
const TimeField = ({ label, value, onChange, currentTime, onPlay, status, hint, lock = {} }) => {
  const { holder, readOnly, onFocus, onBlur, onInput } = lock;
  const [text, setText] = useState(value == null ? "" : secToHMS(value));
  const editing = useRef(false);
  // Follow saved changes (including other people's) unless I'm typing here.
  useEffect(() => { if (!editing.current) setText(value == null ? "" : secToHMS(value)); }, [value]);
  const commit = (raw) => {
    if (raw.trim() === "") { if (value != null) onChange(null); return; }
    const sec = timeToSec(raw.trim());
    if (sec == null || sec < 0) { setText(value == null ? "" : secToHMS(value)); return; }
    if (sec !== value) onChange(sec);
  };
  const nudge = (d) => onChange(Math.max(0, (value ?? currentTime) + d));
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[11px] text-white/45 w-16 shrink-0">{label}</span>
      <input
        value={text}
        readOnly={readOnly}
        onFocus={() => { editing.current = true; onFocus?.(); }}
        onChange={(e) => { setText(e.target.value); onInput?.(); }}
        onBlur={(e) => { editing.current = false; commit(e.target.value); onBlur?.(); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder="H:MM:SS"
        className="w-[88px] px-2 py-1 bg-white/5 border border-white/15 rounded text-kk-cream text-sm font-mono text-center
          placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-kk-gold/50"
      />
      <button onClick={() => onChange(Math.floor(currentTime))} title="Set to the player's current time" disabled={readOnly}
        className={`${btn} px-2 py-1 bg-kk-gold/15 text-kk-gold hover:bg-kk-gold/25`}>
        <FaClock className="text-[9px]" /> Now
      </button>
      {[-5, -1, 1, 5].map((d) => (
        <button key={d} onClick={() => nudge(d)} disabled={value == null || readOnly}
          className={`${btn} px-1.5 py-1 bg-white/5 text-white/60 hover:text-white font-mono`}>
          {d > 0 ? `+${d}` : d}s
        </button>
      ))}
      {value != null && (
        <button onClick={() => onPlay(value)} title="Play from here" className="p-1.5 text-kk-gold/60 hover:text-kk-gold">
          <FaPlay className="text-[9px]" />
        </button>
      )}
      <SaveIndicator status={status} />
      {holder && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: `${holder.color}26`, color: holder.color }}>✎ {holder.name} is editing</span>}
      {hint && <span className="text-[10px] text-white/30">{hint}</span>}
    </div>
  );
};

const LinkToLineup = ({ perf, roster, nameOf, suggested, status, onLink, lock = {} }) => {
  const [choice, setChoice] = useState(suggested || "");
  // The line-up's names load after this first renders; adopt the suggestion
  // when it arrives unless someone has already picked.
  useEffect(() => { if (suggested) setChoice((c) => c || suggested); }, [suggested]);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[11px] text-white/45 w-16 shrink-0">Told by</span>
      <select value={choice} onChange={(e) => setChoice(e.target.value)} disabled={lock.readOnly}
        onFocus={lock.onFocus} onBlur={lock.onBlur}
        className="px-2 py-1 bg-white/5 border border-white/15 rounded text-xs text-kk-cream">
        <option value="">— choose from the line-up —</option>
        {roster.map((r) => <option key={r.id} value={r.storyteller_id}>{nameOf(r.storyteller_id) || r.storyteller_id}</option>)}
      </select>
      {lock.holder && <span className="text-[10px]" style={{ color: lock.holder.color }}>✎ {lock.holder.name}</span>}
      <button onClick={() => choice && onLink(choice)} disabled={!choice || lock.readOnly}
        className={`${btn} bg-kk-gold/15 text-kk-gold hover:bg-kk-gold/25`}>Link to line-up</button>
      {suggested && choice === suggested && <span className="text-[10px] text-white/30">suggested by name</span>}
      <SaveIndicator status={status} />
    </div>
  );
};

const Segment = ({ title, color, children }) => (
  <div className={`border-l-2 ${color} pl-3 py-1.5 space-y-1.5`}>
    <div className="text-xs font-semibold text-white/70">{title}</div>
    {children}
  </div>
);

function EditorWorkspace({ profile, onSignOut }) {
  const { episodeId } = useParams();
  const epId = Number(episodeId);
  const authed = true;
  const admin = isAdmin(profile);
  const db = useMemo(() => dataClientFor(profile), [profile]);

  const [episode, setEpisode] = useState(null);
  const [roster, setRoster] = useState([]);
  const [tellers, setTellers] = useState({});
  const [performances, setPerformances] = useState([]);
  const [stories, setStories] = useState({});
  const [writers, setWriters] = useState({});
  const [storyReels, setStoryReels] = useState({});
  const [jobs, setJobs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);
  const onPlay = useCallback((sec) => playerRef.current?.playFrom(sec), []);

  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [zoomUuid, setZoomUuid] = useState("");
  const [openRun, setOpenRun] = useState(null);

  const { saveStatus, onSaveField, onGenerateReel, handleAddStory, registerReels } = useStoryEditing({
    episodeId, setStories, setPerformances, setWriters, setStoryReels, db,
  });

  // ─── Live collaboration ───────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const pipelineRef = useRef(null);
  const onRowChange = useCallback((table, { eventType, new: row, old }) => {
    if (table === "performances") {
      if (eventType === "DELETE") setPerformances((ps) => ps.filter((p) => p.performance_id !== old?.performance_id));
      else setPerformances((ps) => (ps.some((p) => p.performance_id === row.performance_id)
        ? ps.map((p) => (p.performance_id === row.performance_id ? { ...p, ...row } : p)) : [...ps, row]));
    } else if (table === "stories" && row?.story_id) {
      setStories((m) => (m[row.story_id] ? { ...m, [row.story_id]: { ...m[row.story_id], ...row } } : m));
    } else if (table === "episode_storytellers" && row?.id) {
      setRoster((rs) => rs.map((r) => (r.id === row.id ? { ...r, ...row } : r)));
    } else if (table === "episodes" && row) {
      setEpisode((e) => ({ ...e, ...row }));
    } else if (table === "editor_audit" && row) {
      setHistory((h) => [row, ...h].slice(0, 60));
    } else if (table === "episode_jobs" || table === "pipeline_runs") {
      pipelineRef.current?.();
    }
  }, []);
  const collab = useEpisodeCollab({ db, episodeId: epId, me: profile, onRowChange });

  // ─── Loading ──────────────────────────────────────────────────────────
  const loadPipeline = useCallback(async () => {
    const [{ data: j }, { data: r }] = await Promise.all([
      db.from("episode_jobs").select("job_id,steps,status,requested_by,error_message,result,created_at,updated_at")
        .eq("episode_id", epId).order("job_id", { ascending: false }).limit(12),
      db.from("pipeline_runs").select("run_id,step,status,active,error_message,created_at,finished_at")
        .eq("episode_id", epId).order("run_id", { ascending: false }).limit(40),
    ]);
    setJobs(j || []);
    setRuns(r || []);
    return j || [];
  }, [epId, db]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const { data: ep, error } = await db.from("episodes").select("*").eq("episode_id", epId).single();
      if (error || !ep) throw new Error(error?.message || "Episode not found");
      setEpisode(ep);
      setZoomUuid(ep.zoom_meeting_uuid || "");

      const { data: ros } = await db.from("episode_storytellers")
        .select("id,storyteller_id,sort_order,intro_start,intro_end,discussion_start,discussion_end")
        .eq("episode_id", epId).order("sort_order", { ascending: true });
      setRoster(ros || []);

      const { data: perfs } = await db.from("performances").select("*")
        .eq("episode_id", epId).order("story_start_sec", { ascending: true, nullsFirst: false });
      setPerformances(perfs || []);

      const tellerIds = [...new Set([...(ros || []).map((r) => r.storyteller_id), ...(perfs || []).map((p) => p.storyteller_id)].filter(Boolean))];
      if (tellerIds.length) {
        const { data: st } = await db.from("storytellers")
          .select("storyteller_id,storyteller_telugu,storyteller_english,title,gender,photo_url").in("storyteller_id", tellerIds);
        const m = {}; (st || []).forEach((s) => { m[s.storyteller_id] = s; }); setTellers(m);
      }

      const storyIds = [...new Set((perfs || []).map((p) => p.story_id).filter(Boolean))];
      if (storyIds.length) {
        const { data: sd } = await db.from("stories").select("*").in("story_id", storyIds);
        const sm = {}; (sd || []).forEach((s) => { sm[s.story_id] = s; }); setStories(sm);
        const wids = [...new Set((sd || []).map((s) => s.writer_id).filter(Boolean))];
        if (wids.length) {
          const { data: wd } = await db.from("writers").select("writer_id,name_telugu").in("writer_id", wids);
          const wm = {}; (wd || []).forEach((w) => { wm[w.writer_id] = w.name_telugu; }); setWriters(wm);
        }
        const { data: rd } = await db.from("story_reels").select("*").in("story_id", storyIds).order("created_at", { ascending: false });
        const rm = {}; (rd || []).forEach((r) => { if (!rm[r.story_id]) rm[r.story_id] = r; });
        setStoryReels(rm); registerReels(rm);
      }
      await loadPipeline();
      const { data: hist } = await db.from("editor_audit").select("*")
        .or(`episode_id.eq.${epId}${storyIds.length ? `,and(table_name.eq.stories,row_key.in.(${storyIds.join(",")}))` : ""}`)
        .order("audit_id", { ascending: false }).limit(60);
      setHistory(hist || []);
    } catch (e) {
      setLoadErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [epId, loadPipeline, registerReels, db]);

  useEffect(() => { if (authed) loadAll(); }, [authed, loadAll]);
  pipelineRef.current = loadPipeline;

  // Poll while anything is queued or running; reload data when a job lands.
  const activeJobs = jobs.filter(isActive).length;
  const lastDoneRef = useRef(null);
  useEffect(() => {
    if (!authed || !activeJobs) return undefined;
    const t = setInterval(async () => {
      const j = await loadPipeline();
      const newestDone = j.find((x) => x.status === "done")?.job_id;
      if (newestDone && newestDone !== lastDoneRef.current) {
        lastDoneRef.current = newestDone;
        if (!j.some(isActive)) loadAll();
      }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [authed, activeJobs, loadPipeline, loadAll]);

  // ─── Derived ──────────────────────────────────────────────────────────
  const nameOf = useCallback((sid, perf) => {
    const t = tellers[sid];
    return (t?.storyteller_telugu || t?.storyteller_english || perf?.storyteller_name_telugu || perf?.storyteller_name_english || "").trim();
  }, [tellers]);

  const names = useMemo(() => {
    const m = {};
    roster.forEach((r) => { const n = nameOf(r.storyteller_id); if (n) m[r.storyteller_id] = n; });
    return m;
  }, [roster, nameOf]);

  const perfsByTeller = useMemo(() => {
    const m = {};
    performances.forEach((p) => { if (p.storyteller_id) (m[p.storyteller_id] = m[p.storyteller_id] || []).push(p); });
    return m;
  }, [performances]);

  const rosterIds = useMemo(() => new Set(roster.map((r) => r.storyteller_id)), [roster]);
  const unrostered = performances.filter((p) => !p.storyteller_id || !rosterIds.has(p.storyteller_id));

  const { chapters, dropped } = useMemo(() => normaliseMarkers(
    collectMarkers({ episode, roster, performances, stories, names })
  ), [episode, roster, performances, stories, names]);

  const latestRun = useMemo(() => {
    const m = {};
    runs.forEach((r) => { if (!m[r.step]) m[r.step] = r; });
    return m;
  }, [runs]);

  // ─── Saving timeline values ──────────────────────────────────────────
  const setEpisodeTime = (field, sec) => {
    setEpisode((e) => ({ ...e, [field]: sec }));
    onSaveField(`ep:${field}`, null, { table: "episodes", match: { episode_id: epId }, patch: { [field]: sec } });
  };

  // Discussion times live on episode_storytellers (what chapters read first)
  // and are mirrored onto the storyteller's first performance so the older
  // pages and the reel pipeline see the same value.
  const setRosterTime = (row, field, sec) => {
    setRoster((rs) => rs.map((r) => (r.id === row.id ? { ...r, [field]: sec } : r)));
    const mirror = { discussion_start: "discussion_start", discussion_end: "discussion_end" }[field];
    const firstPerf = (perfsByTeller[row.storyteller_id] || [])[0];
    if (mirror && firstPerf) {
      setPerformances((ps) => ps.map((p) => (p.performance_id === firstPerf.performance_id
        ? { ...p, [`${mirror}_sec`]: sec, [`${mirror}_time`]: sec == null ? null : secToHMS(sec) } : p)));
    }
    onSaveField(`ros:${row.id}:${field}`, async () => {
      const { error } = await db.from("episode_storytellers").update({ [field]: sec }).eq("id", row.id);
      if (error) throw error;
      if (mirror && firstPerf) {
        const { error: e2 } = await db.from("performances").update({
          [`${mirror}_sec`]: sec, [`${mirror}_time`]: sec == null ? null : secToHMS(sec),
        }).eq("performance_id", firstPerf.performance_id);
        if (e2) throw e2;
      }
    });
  };

  const setPerfTime = (perf, which, sec) => {
    const patch = { [`${which}_sec`]: sec, [`${which}_time`]: sec == null ? null : secToHMS(sec) };
    setPerformances((ps) => ps.map((p) => (p.performance_id === perf.performance_id ? { ...p, ...patch } : p)));
    onSaveField(`perf:${perf.performance_id}:${which}`, null, {
      table: "performances", match: { performance_id: perf.performance_id }, patch,
    });
  };

  // Older episodes' performances often have no storyteller_id, so they sit
  // outside the line-up and their chapters carry no name. Linking fixes both.
  const linkPerf = (perf, storytellerId) => {
    const t = tellers[storytellerId];
    const patch = {
      storyteller_id: storytellerId || null,
      ...(t ? { storyteller_name_telugu: t.storyteller_telugu || perf.storyteller_name_telugu,
                storyteller_name_english: t.storyteller_english || perf.storyteller_name_english } : {}),
    };
    setPerformances((ps) => ps.map((p) => (p.performance_id === perf.performance_id ? { ...p, ...patch } : p)));
    onSaveField(`perf:${perf.performance_id}:link`, null, {
      table: "performances", match: { performance_id: perf.performance_id }, patch,
    });
  };

  // Best guess: the line-up member sharing the most name words with the
  // performance's denormalised storyteller name.
  const suggestTeller = (perf) => {
    const words = (x) => new Set(String(x || "").split(/\s+/).filter((w) => w.length > 1));
    const pw = words(`${perf.storyteller_name_telugu || ""} ${perf.storyteller_name_english || ""}`);
    let best = null; let bestN = 0;
    roster.forEach((r) => {
      const t = tellers[r.storyteller_id];
      const tw = words(`${t?.storyteller_telugu || ""} ${t?.storyteller_english || ""}`);
      const n = [...pw].filter((w) => tw.has(w)).length;
      if (n > bestN) { best = r.storyteller_id; bestN = n; }
    });
    return best;
  };

  // ─── Pipeline actions ────────────────────────────────────────────────
  const queue = async (steps, result = null, label = "") => {
    setBusy(label || steps.join("+"));
    setNotice("");
    const { error } = await db.from("episode_jobs").insert({
      episode_id: epId, env: ENV, steps, requested_by: "editor", result,
    });
    setBusy("");
    if (error) { setNotice(`Could not queue: ${error.message}`); return false; }
    setNotice(`Queued: ${steps.map((s) => STEP_LABELS[s] || s).join(" → ")}. Each step runs on its own; this page refreshes as they finish.`);
    await loadPipeline();
    return true;
  };

  const hasAudio = !!episode?.audio_s3_key;
  const processEpisode = () => {
    if (hasAudio) {
      if (!window.confirm("Re-transcribe and re-analyze this episode?\n\nAnalysis can overwrite story titles, timestamps, transcripts and ratings for this episode, including edits made here.")) return;
      queue(["transcribe", "analyze"]);
    } else if (zoomUuid.trim()) {
      queue(["zoom_ingest", "transcribe", "analyze"], { zoom_meeting_uuid: zoomUuid.trim() });
    } else {
      setNotice("No recording in the archive yet and no Zoom meeting UUID — the Zoom webhook normally starts this automatically after the show. Paste the meeting UUID to start it by hand.");
    }
  };
  const rerun = (step) => {
    if (step === "analyze" && !window.confirm("Re-run analysis?\n\nIt can overwrite story titles, timestamps, transcripts and ratings for this episode, including edits made here.")) return;
    if (step === "zoom_ingest") {
      if (!zoomUuid.trim()) { setNotice("Paste the Zoom meeting UUID first."); return; }
      if (!window.confirm("Re-ingest from Zoom?\n\nThe recording must still be in Zoom's cloud (or recovered from its trash). After ingest the Zoom copy moves to Zoom's trash again (30-day recoverable).")) return;
      queue(["zoom_ingest"], { zoom_meeting_uuid: zoomUuid.trim() });
      return;
    }
    queue([step]);
  };
  const publishChapters = async () => {
    if (!reviewed) return;
    if (!window.confirm(`Publish ${chapters.length} chapters to the YouTube video description?\n\nOnly the chapter block is replaced; the rest of the description is left as it is.`)) return;
    if (await queue(["chapters"], null, "chapters")) setReviewed(false);
  };

  // Re-cut each story's transcript from the active transcription using the
  // timestamps on this page (same rule as the pipeline's slice_transcript).
  const recutTranscripts = async () => {
    setBusy("recut");
    setNotice("");
    try {
      const { data, error } = await db.from("pipeline_runs").select("output")
        .eq("episode_id", epId).eq("step", "transcribe").eq("active", true).limit(1).maybeSingle();
      if (error) throw error;
      const segs = data?.output?.segments;
      if (!segs?.length) throw new Error("No finished transcription for this episode yet.");
      const targets = performances.filter((p) => p.story_start_sec != null && p.story_end_sec != null && p.story_end_sec > p.story_start_sec);
      if (!targets.length) throw new Error("No story has both a start and an end time.");
      if (!window.confirm(`Replace the transcript of ${targets.length} stor${targets.length === 1 ? "y" : "ies"} with the text between their current start and end times?`)) { setBusy(""); return; }
      for (const p of targets) {
        const text = sliceTranscript(segs, p.story_start_sec, p.story_end_sec);
        const { error: e2 } = await db.from("performances").update({ transcript: text }).eq("performance_id", p.performance_id);
        if (e2) throw e2;
      }
      setNotice(`Re-cut ${targets.length} story transcript(s) from the timestamps.`);
      await loadAll();
    } catch (e) {
      setNotice(`Re-cut failed: ${e.message}`);
    } finally {
      setBusy("");
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────
  if (loading && !episode) {
    return <div className="min-h-screen bg-kk-ink flex items-center justify-center"><div className="w-14 h-14 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin" /></div>;
  }
  if (loadErr) {
    return (
      <div className="min-h-screen bg-kk-ink flex flex-col items-center justify-center gap-4 text-kk-cream">
        <p>Could not load episode {episodeId}: {loadErr}</p>
        <Link to="/episode-creator" className="px-5 py-2 bg-kk-gold text-kk-ink rounded-full font-semibold">Back to Episodes</Link>
      </div>
    );
  }

  const videoId = episode?.video_id || episode?.youtube_broadcast_id;
  const stepOrder = ["zoom_ingest", "transcribe", "analyze", "chapters"];

  return (
    <div className="min-h-screen bg-kk-ink text-white">
      <div className="sticky top-0 z-30 bg-kk-ink border-b border-kk-gold/10">
        <div className="px-4 py-2 max-w-6xl mx-auto flex items-center justify-between gap-3">
          <Link to="/episode-creator" className="flex items-center gap-2 text-kk-gold/70 hover:text-kk-gold text-sm">
            <FaArrowLeft className="text-xs" /> Episodes
          </Link>
          <div className="flex items-center gap-3">
            <PresenceBar people={collab.people} status={collab.status} />
            <span className="text-white/25 text-xs font-mono hidden sm:inline">Ep {epId}{IS_TEST ? " (TEST DB)" : ""}</span>
            <span className="text-[11px] text-white/50">{profile.name}{admin ? " · admin" : ""}</span>
            <button onClick={onSignOut} title="Sign out" className="text-white/40 hover:text-white"><FaSignOutAlt /></button>
          </div>
        </div>
        {videoId
          ? <CompactYouTubePlayer ref={playerRef} videoId={videoId} onTimeUpdate={setCurrentTime} sticky={false} />
          : <p className="text-center text-xs text-white/40 pb-2">No YouTube video linked yet — timestamps can still be typed in.</p>}
      </div>

      <div className="max-w-4xl mx-auto px-4 py-5 space-y-6">
        <header>
          <h1 className="font-krushna text-kk-cream text-lg leading-snug">{episode.title || `Episode ${epId}`}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-white/45">
            <span>{episode.publish_date}</span>
            {episode.episode_type && <span className="uppercase tracking-wide">{episode.episode_type}</span>}
            <StatusPill status={episodeStage(episode, latestRun, activeJobs)} />
            {videoId && <a className="flex items-center gap-1 text-red-300/80 hover:text-red-300" href={`https://www.youtube.com/watch?v=${videoId}`} target="_blank" rel="noreferrer"><FaYoutube /> YouTube</a>}
            {episode.video_duration_sec && <span>{secToHMS(episode.video_duration_sec)} archived</span>}
            {episode.processed_at && <span>processed {ago(episode.processed_at)}</span>}
            <Link className="text-kk-gold/60 hover:text-kk-gold" to={`/episode-metadata/${epId}`}>reels &amp; highlights →</Link>
          </div>
        </header>

        {notice && <div className="text-xs px-3 py-2 rounded-lg bg-kk-gold/10 border border-kk-gold/20 text-kk-cream/90">{notice}</div>}

        {/* ═══ PIPELINE ═══ (admins only — volunteers edit the timeline and stories) */}
        {admin && <section className={`${card} p-4 space-y-3`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-kk-gold flex items-center gap-2"><FaCogs /> Pipeline</h2>
            <div className="flex items-center gap-2">
              {admin && <button onClick={processEpisode} disabled={!!busy || activeJobs > 0}
                className={`${btn} bg-kk-gold text-kk-ink hover:bg-kk-amber`}>
                {busy && busy !== "recut" ? <FaSpinner className="animate-spin" /> : <FaCogs />} Process episode
              </button>}
              <button onClick={loadPipeline} title="Refresh" className={`${btn} bg-white/5 text-white/60 hover:text-white`}><FaSyncAlt /></button>
            </div>
          </div>
          <p className="text-[11px] text-white/40">
            {hasAudio ? "The recording is archived; Process re-runs transcription and analysis." : "Normally started by Zoom right after the show. Each step runs on its own and the next one starts automatically."}
            {" "}Chapters are never published automatically — review them below first.
          </p>
          <div className="grid sm:grid-cols-4 gap-2">
            {stepOrder.map((step) => {
              const r = latestRun[step] || (step === "zoom_ingest" ? latestRun.download : null);
              return (
                <div key={step} className="rounded-lg bg-white/[0.03] border border-white/5 p-2.5 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/70">{STEP_LABELS[step]}</span>
                    {r ? <StatusPill status={r.status === "running" && isStuck({ ...r, updated_at: r.created_at }) ? "stuck" : r.status} />
                      : <span className="text-[10px] text-white/25">not run</span>}
                  </div>
                  {r && <div className="text-[10px] text-white/30">{ago(r.finished_at || r.created_at)}</div>}
                  {r?.error_message && (
                    <button onClick={() => setOpenRun(openRun === r.run_id ? null : r.run_id)} className="text-[10px] text-red-300/80 flex items-center gap-1">
                      <FaExclamationTriangle /> error {openRun === r.run_id ? "▾" : "▸"}
                    </button>
                  )}
                  {openRun === r?.run_id && <pre className="text-[10px] text-red-200/70 whitespace-pre-wrap max-h-40 overflow-auto">{r.error_message}</pre>}
                  {admin && step !== "chapters" && (
                    <button onClick={() => rerun(step)} disabled={!!busy || activeJobs > 0 || (step !== "zoom_ingest" && !hasAudio)}
                      className={`${btn} bg-white/5 text-white/60 hover:text-white w-full justify-center`}>
                      <FaSyncAlt className="text-[9px]" /> Re-run
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {admin && <div className="flex items-center gap-2">
            <span className="text-[11px] text-white/40 shrink-0">Zoom meeting UUID</span>
            <input value={zoomUuid} onChange={(e) => setZoomUuid(e.target.value)} placeholder="set automatically by the Zoom webhook"
              className="flex-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-xs font-mono text-kk-cream placeholder:text-white/20" />
          </div>}
          {jobs.length > 0 && (
            <div className="text-[11px] text-white/50 space-y-1">
              {jobs.slice(0, 6).map((j) => {
                // A failure a later successful job redid is history, not a problem.
                const replaced = j.status === "failed" && jobs.some((k) => k.job_id > j.job_id && k.status === "done");
                return (
                <div key={j.job_id} className={`flex items-center gap-2 ${replaced ? "opacity-50" : ""}`}>
                  <StatusPill status={replaced ? "replaced" : isStuck(j) ? "stuck" : j.status} />
                  <span className="font-mono text-white/30">#{j.job_id}</span>
                  <span>{(j.steps || []).map((s) => STEP_LABELS[s] || s).join(" → ")}</span>
                  <span className="text-white/25">{j.requested_by} · {ago(j.updated_at || j.created_at)}</span>
                  {j.error_message && <span className={`${replaced ? "text-white/30" : "text-red-300/70"} truncate`}>{j.error_message.slice(0, 120)}</span>}
                </div>
                );
              })}
            </div>
          )}
        </section>}

        {/* ═══ SOCIAL PROMO ═══ (admins only) */}
        {admin && <PromoPanel db={db} episodeId={epId} me={profile} />}

        {/* ═══ TIMELINE ═══ */}
        <section className={`${card} p-4 space-y-4`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-kk-gold">Timeline</h2>
            {admin && <button onClick={recutTranscripts} disabled={!!busy} className={`${btn} bg-white/5 text-white/60 hover:text-white`}
              title="Replace each story's transcript with the transcription between its current start and end">
              {busy === "recut" ? <FaSpinner className="animate-spin" /> : <FaCut />} Re-cut story transcripts from these times
            </button>}
          </div>
          <p className="text-[11px] text-white/40">Times are from the start of the YouTube video. Play the video, then press <b>Now</b> at the right moment; nudge with ±1s / ±5s. Changes save automatically.</p>

          <Segment title="Host introduction" color="border-sky-400/50">
            <TimeField label="Start" value={episode.host_monologue_start} currentTime={currentTime} onPlay={onPlay}
              status={saveStatus["ep:host_monologue_start"]} lock={collab.bind("ep:host_monologue_start")} onChange={(v) => setEpisodeTime("host_monologue_start", v)} />
            <TimeField label="End" value={episode.host_monologue_end} currentTime={currentTime} onPlay={onPlay}
              status={saveStatus["ep:host_monologue_end"]} lock={collab.bind("ep:host_monologue_end")} onChange={(v) => setEpisodeTime("host_monologue_end", v)} />
          </Segment>

          {roster.map((r, i) => {
            const who = nameOf(r.storyteller_id) || `Storyteller ${i + 1}`;
            const perfs = perfsByTeller[r.storyteller_id] || [];
            const firstPerf = perfs[0];
            return (
              <div key={r.id} className="rounded-lg bg-white/[0.02] border border-white/5 p-3 space-y-2">
                <div className="font-krushna text-kk-cream">{i + 1}. {who}</div>
                <Segment title="Introduction of the storyteller" color="border-purple-400/50">
                  <TimeField label="Start" value={r.intro_start} currentTime={currentTime} onPlay={onPlay}
                    status={saveStatus[`ros:${r.id}:intro_start`]} lock={collab.bind(`ros:${r.id}:intro_start`)} onChange={(v) => setRosterTime(r, "intro_start", v)} />
                  <TimeField label="End" value={r.intro_end} currentTime={currentTime} onPlay={onPlay}
                    status={saveStatus[`ros:${r.id}:intro_end`]} lock={collab.bind(`ros:${r.id}:intro_end`)} onChange={(v) => setRosterTime(r, "intro_end", v)} />
                </Segment>
                {perfs.map((p) => (
                  <Segment key={p.performance_id} color="border-kk-gold/60"
                    title={`Story — ${stories[p.story_id]?.title_telugu || "(name it under Stories below)"}`}>
                    <TimeField label="Start" value={p.story_start_sec} currentTime={currentTime} onPlay={onPlay}
                      status={saveStatus[`perf:${p.performance_id}:story_start`]} lock={collab.bind(`perf:${p.performance_id}:story_start`)} onChange={(v) => setPerfTime(p, "story_start", v)} />
                    <TimeField label="End" value={p.story_end_sec} currentTime={currentTime} onPlay={onPlay}
                      status={saveStatus[`perf:${p.performance_id}:story_end`]} lock={collab.bind(`perf:${p.performance_id}:story_end`)} onChange={(v) => setPerfTime(p, "story_end", v)} />
                  </Segment>
                ))}
                {!perfs.length && <p className="text-[11px] text-white/30 pl-3">No story detected for {who} (a guest or analyst, or analysis hasn't run yet).</p>}
                <Segment title="Discussion" color="border-emerald-400/50">
                  <TimeField label="Start" value={r.discussion_start ?? firstPerf?.discussion_start_sec ?? null} currentTime={currentTime} onPlay={onPlay}
                    status={saveStatus[`ros:${r.id}:discussion_start`]} lock={collab.bind(`ros:${r.id}:discussion_start`)} onChange={(v) => setRosterTime(r, "discussion_start", v)}
                    hint={r.discussion_start == null && firstPerf?.discussion_start_sec != null ? "from analysis" : ""} />
                  <TimeField label="End" value={r.discussion_end ?? firstPerf?.discussion_end_sec ?? null} currentTime={currentTime} onPlay={onPlay}
                    status={saveStatus[`ros:${r.id}:discussion_end`]} lock={collab.bind(`ros:${r.id}:discussion_end`)} onChange={(v) => setRosterTime(r, "discussion_end", v)} />
                </Segment>
              </div>
            );
          })}

          {unrostered.length > 0 && (
            <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3 space-y-2">
              <div className="text-xs text-white/60">Stories not linked to anyone in the line-up</div>
              {unrostered.map((p) => (
                <Segment key={p.performance_id} color="border-kk-gold/60"
                  title={`${p.storyteller_name_telugu || p.storyteller_name_english || "?"} — ${stories[p.story_id]?.title_telugu || "untitled"}`}>
                  {roster.length > 0 && (
                    <LinkToLineup perf={p} roster={roster} nameOf={nameOf} suggested={suggestTeller(p)} lock={collab.bind(`perf:${p.performance_id}:link`)}
                      status={saveStatus[`perf:${p.performance_id}:link`]} onLink={(sid) => linkPerf(p, sid)} />
                  )}
                  <TimeField label="Start" value={p.story_start_sec} currentTime={currentTime} onPlay={onPlay}
                    status={saveStatus[`perf:${p.performance_id}:story_start`]} lock={collab.bind(`perf:${p.performance_id}:story_start`)} onChange={(v) => setPerfTime(p, "story_start", v)} />
                  <TimeField label="End" value={p.story_end_sec} currentTime={currentTime} onPlay={onPlay}
                    status={saveStatus[`perf:${p.performance_id}:story_end`]} lock={collab.bind(`perf:${p.performance_id}:story_end`)} onChange={(v) => setPerfTime(p, "story_end", v)} />
                  <TimeField label="Discussion" value={p.discussion_start_sec} currentTime={currentTime} onPlay={onPlay}
                    status={saveStatus[`perf:${p.performance_id}:discussion_start`]} lock={collab.bind(`perf:${p.performance_id}:discussion_start`)} onChange={(v) => setPerfTime(p, "discussion_start", v)} />
                </Segment>
              ))}
            </div>
          )}
        </section>

        {/* ═══ CHAPTERS ═══ */}
        <section className={`${card} p-4 space-y-3`}>
          <h2 className="text-sm font-semibold text-kk-gold flex items-center gap-2"><FaYoutube /> YouTube chapters — preview</h2>
          {chapters.length < YT_MIN_CHAPTERS ? (
            <p className="text-xs text-amber-300/80">YouTube needs at least {YT_MIN_CHAPTERS} chapters; there are {chapters.length}. Fill in the timeline above.</p>
          ) : (
            <ol className="text-sm font-mono space-y-0.5">
              {chapters.map((c, i) => (
                <li key={i} className="flex gap-3">
                  <button onClick={() => onPlay(c.sec)} className="text-kk-gold/80 hover:text-kk-gold w-16 text-right">{chapterTime(c.sec)}</button>
                  <span className="text-kk-cream/90 font-sans">{c.label}</span>
                </li>
              ))}
            </ol>
          )}
          {dropped.length > 0 && (
            <div className="text-[11px] text-amber-300/70">
              Left out (YouTube drops chapters shorter than 10 s):{" "}
              {dropped.map((d) => `${chapterTime(d.sec)} ${d.label}`).join("; ")}
            </div>
          )}
          {!admin && <p className="text-[11px] text-white/40">An admin publishes these to YouTube after review.</p>}
          {admin && <div className="flex items-center gap-3 flex-wrap pt-1">
            <label className="flex items-center gap-2 text-xs text-white/60">
              <input type="checkbox" className="accent-kk-gold" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
              I've checked these against the video
            </label>
            <button onClick={publishChapters} disabled={!reviewed || chapters.length < YT_MIN_CHAPTERS || !videoId || !!busy || activeJobs > 0}
              className={`${btn} bg-red-600 text-white hover:bg-red-500`}>
              <FaYoutube /> Publish chapters to YouTube
            </button>
            {latestRun.chapters?.status === "done" && (
              <span className="text-[11px] text-green-300/70 flex items-center gap-1"><FaCheckCircle /> last published {ago(latestRun.chapters.finished_at)}</span>
            )}
          </div>}
        </section>

        {/* ═══ STORIES ═══ */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-kk-gold">Stories</h2>
          {performances.filter((p) => p.story_id).length === 0 && (
            <p className="text-xs text-white/40">No stories yet — they appear after analysis, or add one per storyteller below.</p>
          )}
          {/* StoryCard copies its props into local state on mount, so only
              mount it once stories/writers/reels have all loaded. */}
          {loading && <p className="text-xs text-white/40 flex items-center gap-2"><FaSpinner className="animate-spin" /> loading stories…</p>}
          {!loading && performances.filter((p) => p.story_id).map((p) => (
            <div key={p.performance_id} className="space-y-1">
              <div className="text-[11px] text-white/45 pl-1">{nameOf(p.storyteller_id, p) || "Storyteller"}</div>
              <StoryCard perf={p} story={stories[p.story_id]} writerName={writers[stories[p.story_id]?.writer_id]}
                storyReel={storyReels[p.story_id]} onSaveField={onSaveField} onGenerateReel={onGenerateReel}
                onSeek={onPlay} currentTime={currentTime} saveStatus={saveStatus} hideTimestamps collab={collab} />
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            {roster.map((r) => (
              <button key={r.id} onClick={() => handleAddStory({
                storyteller_id: r.storyteller_id,
                telugu: tellers[r.storyteller_id]?.storyteller_telugu, english: tellers[r.storyteller_id]?.storyteller_english,
              })} className={`${btn} border border-dashed border-kk-gold/25 text-kk-gold/60 hover:text-kk-gold`}>
                + story for {nameOf(r.storyteller_id) || "storyteller"}
              </button>
            ))}
          </div>
        </section>
        <HistoryPanel history={history} stories={stories} performances={performances} names={names} onPlay={onPlay} />
        {admin && <EditorsPanel me={profile} />}
      </div>
      <div className="h-16" />
    </div>
  );
}

// ─── Who's here ──────────────────────────────────────────────────────────
const PresenceBar = ({ people, status }) => (
  <div className="flex items-center gap-1.5">
    <FaCircle title={status === "live" ? "Live — changes sync instantly" : `Realtime ${status}`}
      className={`text-[7px] ${status === "live" ? "text-green-400" : status === "offline" ? "text-red-400" : "text-amber-400"}`} />
    {people.map((p) => (
      <span key={p.id} title={`${p.name}${p.me ? " (you)" : ""}${p.field ? " — editing" : ""}`}
        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-kk-ink border-2"
        style={{ background: p.color, borderColor: p.field ? "#fff" : "transparent" }}>
        {(p.name || "?").trim().charAt(0).toUpperCase()}
      </span>
    ))}
    {people.length > 1 && (
      <span className="text-[10px] text-white/50 hidden md:inline">
        {people.filter((p) => !p.me).map((p) => p.name).join(", ")} also here
      </span>
    )}
  </div>
);

// ─── Change history ──────────────────────────────────────────────────────
const FIELD_LABELS = {
  story_start_sec: "story start", story_end_sec: "story end",
  discussion_start_sec: "discussion start", discussion_end_sec: "discussion end",
  discussion_start: "discussion start", discussion_end: "discussion end",
  intro_start: "intro start", intro_end: "intro end",
  host_monologue_start: "host intro start", host_monologue_end: "host intro end",
  title_telugu: "story name", title_english: "English title", genre: "genre", writer_id: "writer",
  storyteller_id: "storyteller link", story_id: "story link", is_author: "told own story",
  transcript: "transcript", analysis_text: "analysis", headline: "headline", title: "episode title",
};
const TIME_FIELDS = new Set(["story_start_sec", "story_end_sec", "discussion_start_sec", "discussion_end_sec",
  "discussion_start", "discussion_end", "intro_start", "intro_end", "host_monologue_start", "host_monologue_end"]);

function showValue(field, v) {
  if (v === null || v === undefined) return "—";
  if (TIME_FIELDS.has(field)) return secToHMS(v);
  if (typeof v === "object" && v.chars !== undefined) return `${v.chars} chars`;
  if (typeof v === "boolean") return v ? "yes" : "no";
  const s = String(v);
  return s.length > 40 ? `${s.slice(0, 40)}…` : s;
}

const HistoryPanel = ({ history, stories, performances, names, onPlay }) => {
  const [open, setOpen] = useState(false);
  const whatRow = (h) => {
    if (h.table_name === "stories") return stories[h.row_key]?.title_telugu || `story ${h.row_key}`;
    if (h.table_name === "performances") {
      const p = performances.find((x) => String(x.performance_id) === String(h.row_key));
      return (p && (stories[p.story_id]?.title_telugu || names[p.storyteller_id] || p.storyteller_name_telugu)) || `story row ${h.row_key}`;
    }
    if (h.table_name === "episodes") return "episode";
    return "line-up";
  };
  return (
    <section className={`${card} p-4 space-y-2`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-sm font-semibold text-kk-gold">
        <span className="flex items-center gap-2"><FaHistory /> Change history</span>
        <span className="text-[11px] text-white/40 font-normal">{history.length} recent change(s) {open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="space-y-1 max-h-96 overflow-auto">
          {history.length === 0 && <p className="text-xs text-white/40">No changes recorded yet.</p>}
          {history.map((h) => (
            <div key={h.audit_id} className="text-[11px] text-white/60 flex flex-wrap items-baseline gap-x-2">
              <span className="text-white/30 w-20 shrink-0">{ago(h.created_at)}</span>
              <span className="text-kk-cream/80 font-semibold">{decodeEditorName(h.editor_name) || "system"}</span>
              <span>{FIELD_LABELS[h.field] || h.field} of <span className="font-krushna text-white/75">{whatRow(h)}</span>:</span>
              <span className="font-mono text-white/40">{showValue(h.field, h.old_value)}</span>
              <span>→</span>
              {TIME_FIELDS.has(h.field) && h.new_value != null
                ? <button onClick={() => onPlay(h.new_value)} className="font-mono text-kk-gold/80 hover:text-kk-gold">{showValue(h.field, h.new_value)}</button>
                : <span className="font-mono text-white/80">{showValue(h.field, h.new_value)}</span>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ─── Sign-in wrapper (per-person accounts; see lib/editorSession.js) ─────
export default function EpisodeEditor() {
  const [profile, setProfile] = useState(undefined); // undefined = checking session
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("signin"); // "signin" | "request"
  const [displayName, setDisplayName] = useState("");
  const [password2, setPassword2] = useState("");

  useEffect(() => {
    authClient.auth.getSession().then(({ data }) => setProfile(profileFromUser(data.session?.user) || null));
    const { data: sub } = authClient.auth.onAuthStateChange((_e, session) => setProfile(profileFromUser(session?.user) || null));
    return () => sub.subscription.unsubscribe();
  }, []);

  const signIn = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const { error } = await authClient.auth.signInWithPassword({ email: usernameToEmail(username), password });
    setBusy(false);
    if (error) setErr(/invalid|credentials/i.test(error.message) ? "Wrong username or password." : error.message);
    else setPassword("");
  };
  const signOut = () => authClient.auth.signOut();

  // New people sign up themselves; the account can do nothing until an admin
  // approves it in the Editors panel (db/episode-editor-accounts.sql).
  const requestAccess = async (e) => {
    e.preventDefault();
    setErr("");
    const u = username.trim().toLowerCase();
    if (!/^[a-z0-9._-]{2,30}$/.test(u)) { setErr("Username: 2–30 lowercase letters, digits, dot, dash or underscore (e.g. gowri)."); return; }
    if (!displayName.trim()) { setErr("Please enter your name as it should appear to others."); return; }
    if (password.length < 8) { setErr("Password: at least 8 characters."); return; }
    if (password !== password2) { setErr("The two passwords don't match."); return; }
    setBusy(true);
    const { error } = await authClient.auth.signUp({
      email: usernameToEmail(u), password,
      options: { data: { username: u, name: displayName.trim() } },
    });
    setBusy(false);
    if (error) setErr(/registered|exists/i.test(error.message) ? "That username is taken — choose another, or sign in." : error.message);
    else { setPassword(""); setPassword2(""); }
  };
  const checkAgain = async () => {
    setBusy(true);
    const { data } = await authClient.auth.refreshSession();
    setBusy(false);
    if (data?.session) setProfile(profileFromUser(data.session.user));
  };

  if (profile === undefined) {
    return <div className="min-h-screen bg-kk-ink flex items-center justify-center"><div className="w-14 h-14 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin" /></div>;
  }
  if (!profile) {
    const field = "w-full px-4 py-3 bg-white/5 border border-kk-gold/20 rounded-lg text-white focus:ring-2 focus:ring-kk-gold placeholder:text-white/30";
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center px-4">
        <form onSubmit={mode === "signin" ? signIn : requestAccess} className="bg-white/5 border border-kk-gold/20 rounded-xl p-8 max-w-md w-full space-y-3">
          <h1 className="font-krushna text-kk-gold text-2xl text-center">Episode Editor</h1>
          <p className="text-xs text-white/40 text-center">
            {mode === "signin" ? "Sign in with your username and password." : "Choose a username and password. Bhaskar approves new accounts before they can edit."}
          </p>
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" autoComplete="username" autoCapitalize="none" className={field} />
          {mode === "request" && (
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name (shown to others, e.g. సర్వమంగళ గౌరి)" className={field} />
          )}
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            autoComplete={mode === "signin" ? "current-password" : "new-password"} className={field} />
          {mode === "request" && (
            <input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} placeholder="Password again" autoComplete="new-password" className={field} />
          )}
          <button type="submit" disabled={busy || !username || !password}
            className="w-full bg-kk-gold text-kk-ink py-3 rounded-lg hover:bg-kk-amber font-semibold disabled:opacity-50">
            {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Request access"}
          </button>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <button type="button" onClick={() => { setMode(mode === "signin" ? "request" : "signin"); setErr(""); }}
            className="w-full text-xs text-kk-gold/70 hover:text-kk-gold">
            {mode === "signin" ? "New to the team? Request access" : "Already have an account? Sign in"}
          </button>
        </form>
      </div>
    );
  }
  if (!canEdit(profile)) {
    return (
      <div className="min-h-screen bg-kk-ink flex flex-col items-center justify-center gap-4 text-kk-cream px-4 text-center">
        <p className="max-w-md">Thanks, {profile.name}! Your account is waiting for approval. Once Bhaskar approves it, press <b>Check again</b>.</p>
        <div className="flex gap-3">
          <button onClick={checkAgain} disabled={busy} className="px-5 py-2 bg-kk-gold text-kk-ink rounded-full font-semibold disabled:opacity-50">{busy ? "Checking…" : "Check again"}</button>
          <button onClick={signOut} className="px-5 py-2 bg-white/10 text-kk-cream rounded-full font-semibold">Sign out</button>
        </div>
      </div>
    );
  }
  return <EditorWorkspace profile={profile} onSignOut={signOut} />;
}

// ─── Editors (admin): approve / promote / remove access ─────────────────
// Calls the SECURITY DEFINER functions in db/episode-editor-accounts.sql as
// the signed-in admin (authClient carries their JWT).
const EditorsPanel = ({ me }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    const { data, error } = await authClient.rpc("kk_list_editors");
    if (error) setMsg(error.message); else { setRows(data || []); setMsg(""); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const setRole = async (row, role) => {
    const verb = role === null ? `Remove ${row.name || row.username}'s access? Their edit history is kept.` : null;
    if (verb && !window.confirm(verb)) return;
    const { error } = await authClient.rpc("kk_set_editor_role", { target: row.id, new_role: role });
    if (error) setMsg(error.message); else { setMsg(`${row.name || row.username}: ${role ? `now ${role}` : "access removed"} (takes effect when they press Check again or sign in).`); load(); }
  };
  const pending = rows.filter((r) => !r.role);
  return (
    <section className={`${card} p-4 space-y-2`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-sm font-semibold text-kk-gold">
        <span>Editors (team access)</span>
        <span className="text-[11px] text-white/40 font-normal">{open ? (pending.length ? `${pending.length} waiting for approval ▾` : "▾") : "▸"}</span>
      </button>
      {open && (
        <div className="space-y-1.5">
          {msg && <p className="text-[11px] text-kk-cream/80">{msg}</p>}
          <p className="text-[11px] text-white/40">New people use “Request access” on the sign-in page, then appear here.</p>
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-semibold text-kk-cream w-40 truncate">{r.name || r.username}</span>
              <span className="font-mono text-white/40 w-24 truncate">{r.username}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.role === "admin" ? "bg-kk-gold/20 text-kk-gold" : r.role ? "bg-green-500/15 text-green-300" : "bg-amber-500/15 text-amber-300"}`}>
                {r.role || "waiting"}
              </span>
              <span className="text-white/30">{r.last_sign_in_at ? `seen ${ago(r.last_sign_in_at)}` : "never signed in"}</span>
              <span className="ml-auto flex gap-1.5">
                {!r.role && <button onClick={() => setRole(r, "volunteer")} className={`${btn} bg-green-600 text-white hover:bg-green-500`}>Approve</button>}
                {r.role === "volunteer" && <button onClick={() => setRole(r, "admin")} className={`${btn} bg-white/10 text-white/70 hover:text-white`}>Make admin</button>}
                {r.role === "admin" && r.id !== me.id && <button onClick={() => setRole(r, "volunteer")} className={`${btn} bg-white/10 text-white/70 hover:text-white`}>Make volunteer</button>}
                {r.role && r.id !== me.id && <button onClick={() => setRole(r, null)} className={`${btn} bg-red-600/70 text-white hover:bg-red-500`}>Remove access</button>}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
